import { Dispatch, SetStateAction, useCallback, useState } from "react";
import { useAsyncResult } from "../../Hooks/UseAsyncResult";
import { Ev2ExperimentStage, PipelineRun } from "../../Types";
import { fetchExperimentStatuses } from "../../Api/ev2.api";
import { getRolloutId } from "../../Shared/shared.utils";

export function useFetchEv2Status(
  pipelineRuns: PipelineRun[],
  ev2PortalLinks: Record<number, string | null>,
  setError: Dispatch<SetStateAction<any>>,
  setExpStudioLink?: Dispatch<SetStateAction<string>>,
  setLatestEv2Link?: Dispatch<SetStateAction<string>>,
) {
  const [ev2StatusMap, setEv2StatusMap] = useState<
    Record<number, Ev2ExperimentStage[]>
  >({});
  const [ev2StatusLoading, setEv2StatusLoading] = useState<
    Record<number, boolean>
  >({});

  const func = useCallback(
    async (_disposable, _force) => {
      if (pipelineRuns.length === 0) {
        return;
      }
      // map needed to lookup pipeline ids for rollouts from the fetched statuses
      const rolloutIdPipelineIdMap = new Map<string, string>();
      const sortedRuns = [...pipelineRuns].sort(
        (a, b) =>
          new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime(),
      );
      const latestRun = sortedRuns[0];
      try {
        // Get all rollout IDs from the ev2PortalLinks
        const rolloutIds = Object.entries(ev2PortalLinks)
          .filter(([, link]) => link)
          .map(([pipelineId, link]) => {
            const rolloutId = getRolloutId(link);
            rolloutIdPipelineIdMap.set(rolloutId, pipelineId);
            setEv2StatusLoading((prev) => ({ ...prev, [pipelineId]: true }));
            return rolloutId;
          });
        if (!rolloutIds.length) {
          return;
        }
        // fetch ev2 statuses for rollout ids and process results
        const ev2Statuses = await fetchExperimentStatuses(rolloutIds);
        const ev2StatusEntries = Object.entries(ev2Statuses);
        for (const [rolloutId, status] of ev2StatusEntries) {
          const pipelineId = rolloutIdPipelineIdMap.get(rolloutId);
          if (pipelineId) {
            setEv2StatusMap((prev) => ({
              ...prev,
              [pipelineId]: status,
            }));
            // If this is the latest pipeline run, set the expStudioLink and latestEv2Link
            if (pipelineId === latestRun.id.toString() && status.length > 0) {
              const ev2PortalLink = ev2PortalLinks[Number(pipelineId)];
              const expStudioLink = status.find(
                (status) => status.links.experiment,
              );
              if (expStudioLink && setExpStudioLink) {
                const link = expStudioLink.links.experiment;
                setExpStudioLink(link);
              }
              if (ev2PortalLink && setLatestEv2Link) {
                setLatestEv2Link(ev2PortalLink);
              }
            }
          }
        }
        // Set loading to false for all pipelines that were fetched
        Array.from(rolloutIdPipelineIdMap.values()).forEach((pipelineId) =>
          setEv2StatusLoading((prev) => ({ ...prev, [pipelineId]: false })),
        );
      } catch (error) {
        console.error("Error fetching EV2 statuses", error);
        setError(`Unable to fetch EV2 statuses`);
        // Set loading to false for all pipelines that were fetched
        Array.from(rolloutIdPipelineIdMap.values()).forEach((pipelineId) =>
          setEv2StatusLoading((prev) => ({ ...prev, [pipelineId]: false })),
        );
      }
    },
    [
      pipelineRuns,
      ev2PortalLinks,
      setError,
      setExpStudioLink,
      setLatestEv2Link,
    ],
  );

  const asyncResult = useAsyncResult({
    func,
    setError,
  });

  return {
    ...asyncResult,
    ev2StatusMap,
    ev2StatusLoading,
  };
}
